import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage } from 'src/lib/toastMessage';

/** Parse comma-separated campaign IDs from the raw string */
const parseCampaignIds = (raw = '') =>
  raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

// ─────────────────────────────────────────────────────────────────────────────
// Add Node Dialog
// ─────────────────────────────────────────────────────────────────────────────
function AddNodeDialog({ open, onClose, onAdd, existingNodeNames }) {
  const [nodeName, setNodeName] = useState('');
  const [validKeysStr, setValidKeysStr] = useState('');
  const [nodeError, setNodeError] = useState('');

  useEffect(() => {
    if (open) {
      setNodeName('');
      setValidKeysStr('');
      setNodeError('');
    }
  }, [open]);

  const handleAdd = () => {
    let name = nodeName.trim();
    if (!name) {
      setNodeError('Node name is required');
      return;
    }
    if (!name.startsWith('#')) {
      name = `#${name}`;
    }
    if (existingNodeNames.includes(name)) {
      setNodeError(`Node ${name} already exists`);
      return;
    }

    const validKeys = validKeysStr
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    onAdd(name, validKeys);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add DTMF Node</DialogTitle>
      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Node Name"
          value={nodeName}
          onChange={(e) => {
            setNodeName(e.target.value);
            setNodeError('');
          }}
          error={Boolean(nodeError)}
          helperText={nodeError || 'e.g. #4, #1'}
          fullWidth
          size="small"
          autoFocus
          placeholder="e.g. #4"
        />
        <TextField
          label="Valid Keys in IVR (optional)"
          value={validKeysStr}
          onChange={(e) => setValidKeysStr(e.target.value)}
          helperText="Comma-separated keys configured in IVR (e.g. 1, 2)"
          fullWidth
          size="small"
          placeholder="e.g. 1, 2"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleAdd} variant="contained">
          Add Node
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddNodeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  existingNodeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
// Add / Update Key Dialog
// ─────────────────────────────────────────────────────────────────────────────
function AddKeyDialog({ open, onClose, onAdd, existingKeys, allValidKeys }) {
  const [key, setKey] = useState('');
  const [pct, setPct] = useState('');
  const [keyError, setKeyError] = useState('');
  const [pctError, setPctError] = useState('');

  // Reset on open
  useEffect(() => {
    if (open) {
      setKey('');
      setPct('');
      setKeyError('');
      setPctError('');
    }
  }, [open]);

  const validate = () => {
    let valid = true;
    if (!key.trim()) {
      setKeyError('DTMF key is required');
      valid = false;
    } else {
      setKeyError('');
    }
    const num = parseFloat(pct);
    if (pct === '' || isNaN(num)) {
      setPctError('Percentage is required');
      valid = false;
    } else if (num < 0) {
      setPctError('Percentage cannot be negative');
      valid = false;
    } else if (num > 100) {
      setPctError('Percentage cannot exceed 100');
      valid = false;
    } else {
      setPctError('');
    }
    return valid;
  };

  const handleAdd = () => {
    if (!validate()) return;
    onAdd(key.trim(), parseFloat(parseFloat(pct).toFixed(2)));
    onClose();
  };

  const isIvrValidKey =
    key.trim() !== '' &&
    allValidKeys &&
    allValidKeys.some((vk) => String(vk) === String(key.trim()));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add / Update DTMF Key</DialogTitle>
      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="DTMF Key"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          error={Boolean(keyError)}
          helperText={keyError || 'e.g. 1, 2, *, #'}
          fullWidth
          size="small"
          autoFocus
          placeholder="e.g. 1"
        />
        <TextField
          label="Percentage"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          error={Boolean(pctError)}
          helperText={pctError || 'Value between 0 and 100'}
          type="number"
          inputProps={{ min: 0, max: 100, step: 'any' }}
          fullWidth
          size="small"
          placeholder="e.g. 23"
          InputProps={{ endAdornment: <Typography variant="body2" sx={{ ml: 0.5 }}>%</Typography> }}
        />
        {allValidKeys && allValidKeys.length > 0 && key.trim() !== '' && (
          <Typography variant="caption" color={isIvrValidKey ? 'success.main' : 'text.secondary'}>
            {isIvrValidKey
              ? '✓ This matches a configured valid IVR key.'
              : `Note: Key "${key.trim()}" is not in IVR valid keys (${allValidKeys.join(', ')}). It will be marked as Invalid press.`}
          </Typography>
        )}
        {existingKeys?.includes(key.trim()) && key.trim() !== '' && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            This key already exists. Adding it will override the current percentage.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleAdd} variant="contained">
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddKeyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  existingKeys: PropTypes.arrayOf(PropTypes.string),
  allValidKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

// ─────────────────────────────────────────────────────────────────────────────
// Node Table Component
// ─────────────────────────────────────────────────────────────────────────────
function NodeTable({
  nodeName,
  allValidKeys = [],
  valid = {},
  invalid = {},
  data = {},
  pendingChanges = {},
  onAdd,
  onRemovePending,
  onDeleteNode,
  isCustom = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Combine original valid, invalid, and any legacy data
  const baseData = { ...(data || {}), ...(valid || {}), ...(invalid || {}) };
  const merged = { ...baseData, ...pendingChanges };

  // Sort keys numerically if numbers, else alphabetically
  const allKeys = Object.keys(merged).sort((a, b) => {
    const numA = Number(a);
    const numB = Number(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  const existingKeys = Object.keys(baseData);

  // Helper to determine whether a key is valid or invalid press
  const isKeyValid = (k) => {
    if (valid && Object.prototype.hasOwnProperty.call(valid, k)) return true;
    if (invalid && Object.prototype.hasOwnProperty.call(invalid, k)) return false;
    if (allValidKeys && allValidKeys.some((vk) => String(vk) === String(k))) return true;
    return false;
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Node Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            Node: {nodeName}
          </Typography>

          {/* Valid keys present in IVR */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Valid Keys in IVR:
            </Typography>
            {allValidKeys && allValidKeys.length > 0 ? (
              allValidKeys.map((vk) => (
                <Chip
                  key={vk}
                  label={vk}
                  size="small"
                  color="success"
                  variant="soft"
                  sx={{ fontWeight: 700, height: 22 }}
                />
              ))
            ) : (
              <Chip
                label="None"
                size="small"
                variant="outlined"
                sx={{ height: 20, color: 'text.disabled', fontSize: '0.75rem' }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => setDialogOpen(true)}
          >
            Add Key
          </Button>
          {isCustom && onDeleteNode && (
            <Tooltip title="Delete Node">
              <IconButton size="small" color="error" onClick={() => onDeleteNode(nodeName)}>
                <Iconify icon="eva:trash-2-outline" width={18} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Keys Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>DTMF Key</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Press Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Percentage</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, width: 90 }}>Status</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {allKeys.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.disabled', py: 2 }}>
                  No DTMF keys for this node. Click &ldquo;Add Key&rdquo; to add one.
                </TableCell>
              </TableRow>
            )}
            {allKeys.map((k) => {
              const isPending = Object.prototype.hasOwnProperty.call(pendingChanges, k);
              const isOverride = isPending && Object.prototype.hasOwnProperty.call(baseData, k);
              const isNew = isPending && !Object.prototype.hasOwnProperty.call(baseData, k);
              const displayValue = merged[k];
              const validPress = isKeyValid(k);

              return (
                <TableRow
                  key={k}
                  sx={{
                    bgcolor: isPending ? 'action.selected' : undefined,
                  }}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.9rem' }}>
                    {k}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={validPress ? 'Valid' : 'Invalid'}
                      size="small"
                      color={validPress ? 'success' : 'error'}
                      variant="soft"
                      sx={{ fontWeight: 600, minWidth: 64 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {isPending ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {isOverride && (
                          <Typography
                            variant="caption"
                            sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
                          >
                            {baseData[k]}%
                          </Typography>
                        )}
                        <Typography variant="body2" color="primary.main" fontWeight={600}>
                          {displayValue}%
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">{displayValue}%</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isNew && <Chip label="New" size="small" color="success" />}
                    {isOverride && <Chip label="Modified" size="small" color="warning" />}
                  </TableCell>
                  <TableCell padding="checkbox">
                    {isPending && (
                      <Tooltip title="Undo this change">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onRemovePending(nodeName, k)}
                        >
                          <Iconify icon="eva:close-fill" width={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AddKeyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={(k, pct) => onAdd(nodeName, k, pct)}
        existingKeys={existingKeys}
        allValidKeys={allValidKeys}
      />
    </Box>
  );
}

NodeTable.propTypes = {
  nodeName: PropTypes.string.isRequired,
  allValidKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  valid: PropTypes.object,
  invalid: PropTypes.object,
  data: PropTypes.object,
  pendingChanges: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemovePending: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func,
  isCustom: PropTypes.bool,
};

// ─────────────────────────────────────────────────────────────────────────────
// Main DtmfUpdateCard
// ─────────────────────────────────────────────────────────────────────────────
export default function DtmfUpdateCard({ campaignIdsRaw, onNodesChange }) {
  const campaignIds = parseCampaignIds(campaignIdsRaw);
  const isSingleCampaign = campaignIds.length === 1;

  // API state
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [dtmfData, setDtmfData] = useState([]); // Array<{ node, allValidKeys, valid, invalid, data }>
  const [customNodes, setCustomNodes] = useState([]); // User-added nodes
  const [addNodeDialogOpen, setAddNodeDialogOpen] = useState(false);

  // Pending manual changes: { [nodeName]: { [key]: pct } }
  const [pendingChanges, setPendingChanges] = useState({});

  // ── Fetch DTMF data when a single campaign is selected ─────────────────────
  useEffect(() => {
    if (!isSingleCampaign) {
      setDtmfData([]);
      setCustomNodes([]);
      setPendingChanges({});
      setApiError('');
      return;
    }

    const campaignId = campaignIds[0];
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setApiError('');
      setDtmfData([]);
      setCustomNodes([]);
      setPendingChanges({});
      try {
        const response = await axiosPostCall('getCampaignDtmfPressCount', { campaignId });
        if (!cancelled) {
          const rawNodes = response?.dtmfPressCount || [];
          const formatted = rawNodes.map((item) => ({
            node: item.node,
            allValidKeys: item.allValidKeys || [],
            valid: item.valid || {},
            invalid: item.invalid || {},
            data: {
              ...(item.data || {}),
              ...(item.valid || {}),
              ...(item.invalid || {}),
            },
          }));
          setDtmfData(formatted);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err?.message || 'Failed to fetch DTMF data';
          setApiError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [campaignIdsRaw]);

  // Combine API nodes and custom nodes
  const allNodes = useMemo(() => {
    const list = [...dtmfData];
    customNodes.forEach((cn) => {
      if (!list.some((n) => n.node === cn.node)) {
        list.push(cn);
      }
    });
    return list;
  }, [dtmfData, customNodes]);

  const existingNodeNames = useMemo(() => allNodes.map((n) => n.node), [allNodes]);

  // ── Propagate changes up via onNodesChange ─────────────────────────────────
  useEffect(() => {
    // Only send nodes that have pending (new / modified) keys.
    // Do NOT include the original API data — the backend only needs the diff.
    const changedNodes = Object.entries(pendingChanges)
      .filter(([, keys]) => Object.keys(keys).length > 0)
      .map(([nodeName, keys]) => ({ node: nodeName, data: keys }));

    onNodesChange({
      nodes: changedNodes,
    });
  }, [pendingChanges, onNodesChange]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAddKey = useCallback((nodeName, key, pct) => {
    setPendingChanges((prev) => ({
      ...prev,
      [nodeName]: {
        ...(prev[nodeName] || {}),
        [key]: pct,
      },
    }));
  }, []);

  const handleRemovePending = useCallback((nodeName, key) => {
    setPendingChanges((prev) => {
      const nodeChanges = { ...(prev[nodeName] || {}) };
      delete nodeChanges[key];
      return { ...prev, [nodeName]: nodeChanges };
    });
  }, []);

  const handleAddNode = useCallback((nodeName, validKeys) => {
    setCustomNodes((prev) => [
      ...prev,
      {
        node: nodeName,
        allValidKeys: validKeys || [],
        valid: {},
        invalid: {},
        data: {},
        isCustom: true,
      },
    ]);
  }, []);

  const handleRemoveNode = useCallback((nodeName) => {
    setCustomNodes((prev) => prev.filter((n) => n.node !== nodeName));
    setPendingChanges((prev) => {
      const next = { ...prev };
      delete next[nodeName];
      return next;
    });
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  // Multiple campaigns selected — warn and stop
  if (campaignIds.length > 1) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 3,
          borderRadius: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          DTMF Update
        </Typography>
        <Alert severity="warning">
          <AlertTitle>Multiple campaigns selected</AlertTitle>
          DTMF Update supports only <strong>one campaign at a time</strong>. Please enter a single
          campaign ID to use this feature.
        </Alert>
      </Box>
    );
  }

  // No campaign entered yet
  if (campaignIds.length === 0) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 3,
          borderRadius: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          DTMF Update
        </Typography>
        <Alert severity="info">Enter a single campaign ID above to load DTMF data.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 3,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="subtitle1">DTMF Update</Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => setAddNodeDialogOpen(true)}
          disabled={loading}
        >
          Add Node
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Fetching DTMF data for campaign {campaignIds[0]}…
          </Typography>
        </Box>
      )}

      {/* API error */}
      {!loading && apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      {/* Empty response & no custom nodes */}
      {!loading && !apiError && allNodes.length === 0 && (
        <Alert severity="info">
          No DTMF data found for campaign {campaignIds[0]}. You can add a node using the &ldquo;Add Node&rdquo; button above.
        </Alert>
      )}

      {/* Node tables */}
      {!loading &&
        allNodes.map((nodeItem) => (
          <NodeTable
            key={nodeItem.node}
            nodeName={nodeItem.node}
            allValidKeys={nodeItem.allValidKeys || []}
            valid={nodeItem.valid || {}}
            invalid={nodeItem.invalid || {}}
            data={nodeItem.data || {}}
            pendingChanges={pendingChanges[nodeItem.node] || {}}
            onAdd={handleAddKey}
            onRemovePending={handleRemovePending}
            onDeleteNode={handleRemoveNode}
            isCustom={Boolean(nodeItem.isCustom)}
          />
        ))}

      {/* Pending changes for nodes not in allNodes (edge case safety) */}
      {!loading &&
        Object.keys(pendingChanges)
          .filter((n) => !allNodes.find((d) => d.node === n))
          .map((nodeName) => (
            <NodeTable
              key={nodeName}
              nodeName={nodeName}
              allValidKeys={[]}
              valid={{}}
              invalid={{}}
              data={{}}
              pendingChanges={pendingChanges[nodeName] || {}}
              onAdd={handleAddKey}
              onRemovePending={handleRemovePending}
              onDeleteNode={handleRemoveNode}
              isCustom
            />
          ))}

      {/* Add Node Dialog */}
      <AddNodeDialog
        open={addNodeDialogOpen}
        onClose={() => setAddNodeDialogOpen(false)}
        onAdd={handleAddNode}
        existingNodeNames={existingNodeNames}
      />
    </Box>
  );
}

DtmfUpdateCard.propTypes = {
  campaignIdsRaw: PropTypes.string,
  onNodesChange: PropTypes.func.isRequired,
};
